// =================================================================================
// File: src/components/Cards/VisitorStudioCards.jsx
// Description: Shared card components for visitor workshop and studio space listings
// =================================================================================

import React from "react";
import { motion } from "framer-motion";
import { Calendar, Clock, Users, DollarSign, MapPin } from "lucide-react";
import { formatDateRange, formatPrice } from "../../utils/formatters";

export function VisitorWorkshopCard({ workshop, onBook }) {
  const {
    title,
    start_at,
    end_at,
    capacity,
    seats_taken = 0,
    price_cents,
    currency = "USD",
    skill_level,
    hero_image_url,
  } = workshop || {};

  const seatsAvailable = (capacity || 0) - (seats_taken || 0);
  const price = price_cents ? price_cents / 100 : 0;
  const startDate = start_at ? new Date(start_at) : null;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="mudo-glass-panel mudo-glass-interactive overflow-hidden group"
    >
      <div className="h-2 w-full bg-gradient-to-r from-purple-500 to-pink-500" />

      {hero_image_url && (
        <div className="relative h-48 overflow-hidden">
          <img
            src={hero_image_url}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        </div>
      )}

      <div className="p-6">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2 text-white/50">
            <Calendar className="w-4 h-4" />
            <span className="text-xs font-medium">Workshop</span>
          </div>
          {skill_level && (
            <div className="mudo-glass-pill px-3 py-1 text-xs font-medium text-white/90">
              {skill_level}
            </div>
          )}
        </div>

        <h4 className="text-xl font-bold mb-3 group-hover:text-white/90 transition-colors">
          {title || "Workshop"}
        </h4>

        {startDate && (
          <div className="flex items-center gap-2 text-white/60 mb-3 text-sm">
            <Clock className="w-4 h-4" />
            <span>{startDate.toLocaleString()}</span>
          </div>
        )}

        <div className="flex items-center gap-2 text-white/60 text-sm mb-4">
          <Users className="w-4 h-4" />
          <span>
            {seatsAvailable > 0
              ? `${seatsAvailable} spot${seatsAvailable === 1 ? "" : "s"} available`
              : "Fully booked"}
          </span>
        </div>

        {price > 0 && (
          <div className="flex items-center gap-2 mb-6">
            <DollarSign className="w-5 h-5 text-white/50" />
            <div>
              <span className="text-2xl font-bold text-white">{formatPrice(price_cents || 0, currency)}</span>
              <span className="text-sm text-white/60"> per person</span>
            </div>
          </div>
        )}

        <button
          onClick={onBook}
          disabled={seatsAvailable <= 0}
          className="w-full py-3 rounded-xl font-semibold text-white transition-all duration-200 hover:scale-[1.02] hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            background:
              seatsAvailable > 0
                ? "linear-gradient(135deg, #a855f7, #ec4899)"
                : "linear-gradient(135deg, #6b7280, #4b5563)",
          }}
        >
          {seatsAvailable > 0 ? "View & Book" : "Fully Booked"}
        </button>
      </div>
    </motion.div>
  );
}

export function VisitorSpaceCard({ space, onBook }) {
  const {
    name,
    location,
    description,
    tags = [],
    capacity = {},
    pricing = {},
    availability = {},
    accentColor,
  } = space || {};

  const priceParts = [];
  if (pricing?.perHour) {
    priceParts.push(`${formatPrice(pricing.perHour * 100, pricing.currency || "USD")} / hour`);
  }
  if (pricing?.perDay) {
    priceParts.push(`${formatPrice(pricing.perDay * 100, pricing.currency || "USD")} / day`);
  }

  return (
    <div className="mudo-glass-panel mudo-glass-interactive overflow-hidden group">
      <div
        className="h-2 w-full"
        style={{
          background: accentColor || "linear-gradient(90deg, #f97316, #fb923c)",
        }}
      />

      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2 text-white/50">
            <Clock className="w-4 h-4" />
            <span className="text-xs font-medium">Studio Space</span>
          </div>
          {availability?.statusBadge && (
            <div className="mudo-glass-pill px-3 py-1 text-xs font-medium text-white/90">
              {availability.statusBadge}
            </div>
          )}
        </div>

        <h4 className="text-xl font-bold mb-2 group-hover:text-white/90 transition-colors">
          {name || "Studio Space"}
        </h4>

        {location && (
          <div className="flex items-center gap-2 text-white/60 mb-4 text-sm">
            <MapPin className="w-4 h-4" />
            <span>{location}</span>
          </div>
        )}

        {description && (
          <p className="text-white/70 text-sm mb-4 line-clamp-2">{description}</p>
        )}

        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {tags.slice(0, 3).map((tag, idx) => (
              <span
                key={idx}
                className="px-3 py-1 rounded-full text-xs font-medium bg-white/10 border border-white/20 text-white/80"
              >
                {tag}
              </span>
            ))}
            {tags.length > 3 && (
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-white/10 border border-white/20 text-white/60">
                +{tags.length - 3}
              </span>
            )}
          </div>
        )}

        {capacity?.seats && (
          <div className="flex items-center gap-2 text-white/60 text-sm mb-4">
            <Users className="w-4 h-4" />
            <span>
              Up to {capacity.seats} {capacity.seats === 1 ? "person" : "people"}
            </span>
          </div>
        )}

        <div className="flex items-center gap-4 mb-6">
          <DollarSign className="w-5 h-5 text-white/50" />
          <div className="flex flex-wrap gap-3">
            {priceParts.map((part) => (
              <div key={part} className="text-sm text-white/60">
                {part}
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={(e) => {
            e.preventDefault();
            onBook();
          }}
          className="w-full py-3 rounded-xl font-semibold text-white transition-all duration-200 hover:scale-[1.02] hover:shadow-xl"
          style={{
            background: accentColor || "linear-gradient(135deg, #f97316, #fb923c)",
          }}
        >
          View Availability &amp; Book
        </button>
      </div>
    </div>
  );
}
